import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectCanCreateOrders,
  createTakeAwayOrderForCurrentOutlet,
  fetchTakeAwayOrdersForOutlet,
  selectIsCreatingTakeAwayOrder,
  selectIsTakeAwayOrdersLoading,
  selectSelectedTakeAwayId,
  selectTakeAwayOrderById,
  selectTakeAwayOrders,
} from '@store/runningOrder';
import { selectOutletId } from '@store/auth/authSelectors';
import { formatAppCurrency } from '@/renderer/lib/currency';
import { Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export const TakeAwayPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);
  const selectedOrderId = useAppSelector(selectSelectedTakeAwayId);
  const orders = useAppSelector(selectTakeAwayOrders);
  const isLoading = useAppSelector(selectIsTakeAwayOrdersLoading);
  const isCreating = useAppSelector(selectIsCreatingTakeAwayOrder);
  const canCreateOrders = useAppSelector(selectCanCreateOrders);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!outletId) return;
    dispatch(fetchTakeAwayOrdersForOutlet(outletId));
  }, [dispatch, outletId]);

  const filteredOrders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return orders;

    return orders.filter((order) => {
      const billNumberMatch = order.bill_number.toString().includes(query);
      const waiterMatch = (order.waiter_name || '')
        .toLowerCase()
        .includes(query);
      return billNumberMatch || waiterMatch;
    });
  }, [orders, searchQuery]);

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 p-2">
        <button
          type="button"
          onClick={() => dispatch(createTakeAwayOrderForCurrentOutlet())}
          disabled={isCreating || !outletId || !canCreateOrders}
          title={
            canCreateOrders
              ? 'Create a new order'
              : 'Your account cannot create orders'
          }
          className="flex h-8 items-center gap-1 rounded border border-none bg-primary px-3 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary/80"
        >
          <Plus size={14} />
          <span>{isCreating ? 'Creating...' : 'Create'}</span>
        </button>

        <div className="flex-1 relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-full rounded border border-gray-300 bg-white pl-8 pr-3 text-xs text-gray-700 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            aria-label="Search take-away orders"
          />
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-2">
        {isLoading ? (
          <p className="p-2 text-center text-xs italic text-gray-500">
            Loading take-away orders...
          </p>
        ) : filteredOrders.length === 0 ? (
          <p className="p-2 text-center text-xs italic text-gray-500">
            No take-away orders
          </p>
        ) : (
          filteredOrders.map((order) => (
            <button
              key={order.order_id}
              type="button"
              className={`flex w-full flex-col gap-1 rounded-md border bg-white px-3 py-2 text-left transition-colors ${
                selectedOrderId === order.order_id
                  ? 'border-gray-700 ring-1 ring-gray-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => dispatch(selectTakeAwayOrderById(order.order_id))}
            >
              <span className="text-sm font-semibold text-gray-900">
                #{order.bill_number}
              </span>
              <span className="text-xs font-semibold text-gray-700">
                {formatAppCurrency(order.net_amount)}
              </span>
              <span className="truncate text-xs text-gray-500">
                {order.waiter_name || 'Unknown'} • {order.outlet_name}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
